

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTripComponentModal from 'components/NewTripComponentModal';
import MDBadge from "components/MDBadge";

const TripComponents = () => {
    const [components, setComponents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('trip_components');

    useEffect(() => {
        fetchComponents();
    }, []);

    const fetchComponents = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/trip-components/');
            setComponents(response.data);
        } catch (error) {
            console.error('Error fetching Trip Logistics:', error);
            setSnackbar({ open: true, message: 'Error loading Trip Logistics', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveComponent = async (payload) => {
        try {
            await axiosInstance.post('/trip-components/', payload);
            setSnackbar({ open: true, message: 'Trip Logistics created successfully', severity: 'success' });
            fetchComponents();
        } catch (error) {
            console.error('Error saving Trip Logistics:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/trip-components/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Category", accessor: "category" },
        { Header: "Trip ID", accessor: "trip_id" },
        {
            Header: "Price",
            accessor: "price",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text">
                    {value !== null && value !== undefined ? `$${value}` : '-'}
                </MDTypography>
            )
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <MDBadge
                    badgeContent={value}
                    color={value === "Active" ? "success" : "secondary"}
                    variant="gradient"
                    size="sm"
                />
            )
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: components,
    }), [components]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Trip Logistics</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Trip Logistics
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : components.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Trip Logistics" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTripComponentModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveComponent}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TripComponents;
